import { useEffect, useState } from 'react';
import type { Artifact } from '../lib/artifacts';
import { getHighlighter, normalizeLang } from '../lib/highlighter';

/**
 * Read-only code view for an artifact. Reuses the shiki highlighter
 * instance (already on the page for inline markdown code blocks).
 * No editor — edit-in-place is Phase 3.
 */
export function ArtifactCodeView({ artifact }: { artifact: Artifact }) {
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const hl = await getHighlighter();
        const out = hl.codeToHtml(artifact.body, {
          lang: normalizeLang(artifact.lang),
          themes: { light: 'github-light', dark: 'github-dark' },
        });
        if (!cancelled) setHtml(out);
      } catch {
        /* fall back to plain pre */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [artifact.body, artifact.lang]);

  return (
    <div className="h-full overflow-auto bg-subtle">
      {html ? (
        <ShikiHtml html={html} />
      ) : (
        <pre className="px-4 py-3 font-mono text-[13px] leading-relaxed">
          <code>{artifact.body}</code>
        </pre>
      )}
    </div>
  );
}

function ShikiHtml({ html }: { html: string }) {
  // biome-ignore lint/security/noDangerouslySetInnerHtml: Shiki output
  return <div className="mc-code" dangerouslySetInnerHTML={{ __html: html }} />;
}
